import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { Box, Button, useTheme } from 'hephaestus/web';
import type { ChangeEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  RunReportAssetFilterOption,
  RunReportModelFilterOption,
  RunReportPortfolioFilters,
} from '../../types/runTable.types';
import { hasActivePortfolioFilters } from '../../utils/runTable/runReportPortfolioFilters';

export type BulkRebalancePortfolioFilterBarProps = {
  searchPlaceholder: string;
  searchTestId: string;
  modelTestId: string;
  assetTestId: string;
  clearTestId: string;
  value: RunReportPortfolioFilters;
  modelOptions: RunReportModelFilterOption[];
  assetOptions: RunReportAssetFilterOption[];
  onChange: (value: RunReportPortfolioFilters) => void;
  showClear?: boolean;
  leading?: ReactNode;
  trailing?: ReactNode;
};

export const BulkRebalancePortfolioFilterBar = ({
  searchPlaceholder,
  searchTestId,
  modelTestId,
  assetTestId,
  clearTestId,
  value,
  modelOptions,
  assetOptions,
  onChange,
  showClear = true,
  leading,
  trailing,
}: BulkRebalancePortfolioFilterBarProps) => {
  const { t } = useTranslation(['bulkRebalance']);
  const theme = useTheme();
  const selectedModel = modelOptions.find(
    (option) => option.id === value.modelPortfolioId,
  );
  const selectedAsset = assetOptions.find(
    (option) => option.financialProductId === value.financialProductId,
  );

  return (
    <Box
      data-testid='bulk-rebalance-portfolio-filter-bar'
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        flexWrap: 'wrap',
        gap: 1.5,
        px: 1.5,
        py: 1,
        border: '1px solid',
        borderColor: theme.color.outlineVariant,
        borderRadius: theme.borderRadius.md,
        bgcolor: theme.color.neutralHover,
      }}
    >
      {leading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 0.5,
            mr: 0.5,
            pb: 0.25,
          }}
        >
          {leading}
        </Box>
      ) : null}
      <TextField
        label=''
        size='small'
        placeholder={searchPlaceholder}
        value={value.searchQuery ?? ''}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          onChange({
            ...value,
            searchQuery: event.target.value,
          })
        }
        leadingIcon='search'
        sx={{ minWidth: 200, flex: '1 1 200px' }}
        testId={searchTestId}
      />

      {modelOptions.length > 0 && (
        <SelectField
          label={t('reports.filterModelLabel')}
          placeholder={t('reports.filterModelAll')}
          size='small'
          value={value.modelPortfolioId ?? ''}
          onChange={(event: { target: { value: string } }) =>
            onChange({
              ...value,
              modelPortfolioId: event.target.value || undefined,
            })
          }
          clearable
          SelectProps={{ displayEmpty: true }}
          renderValue={() =>
            selectedModel
              ? t('reports.filterModelSelected', {
                  label: selectedModel.label,
                  count: selectedModel.count,
                })
              : t('reports.filterModelAll')
          }
          sx={{ minWidth: 180, flexShrink: 0 }}
          testId={modelTestId}
        >
          {modelOptions.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {t('reports.filterModelOption', {
                label: option.label,
                count: option.count,
              })}
            </MenuItem>
          ))}
        </SelectField>
      )}

      {assetOptions.length > 0 && (
        <SelectField
          label={t('reports.filterAssetLabel')}
          placeholder={t('reports.filterAssetAll')}
          size='small'
          value={value.financialProductId ?? ''}
          onChange={(event: { target: { value: string } }) =>
            onChange({
              ...value,
              financialProductId: event.target.value || undefined,
            })
          }
          clearable
          SelectProps={{ displayEmpty: true }}
          renderValue={() =>
            selectedAsset
              ? t('reports.filterAssetSelected', {
                  ticker: selectedAsset.ticker,
                  count: selectedAsset.count,
                })
              : t('reports.filterAssetAll')
          }
          sx={{ minWidth: 160, flexShrink: 0 }}
          testId={assetTestId}
        >
          {assetOptions.map((option) => (
            <MenuItem
              key={option.financialProductId}
              value={option.financialProductId}
            >
              {t('reports.filterAssetOption', {
                ticker: option.ticker,
                count: option.count,
              })}
            </MenuItem>
          ))}
        </SelectField>
      )}

      {showClear && hasActivePortfolioFilters(value) && (
        <Button
          variant='text'
          size='small'
          onClick={() => onChange({})}
          data-testid={clearTestId}
        >
          {t('reports.filterClear')}
        </Button>
      )}
      {trailing ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pb: 0.25,
          }}
        >
          {trailing}
        </Box>
      ) : null}
    </Box>
  );
};
